import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bike, Check, CheckCircle2, Copy, Minus, Plus, Search, ShoppingBag, UtensilsCrossed, X } from "lucide-react";
import { ORDER_CATEGORIES, ORDER_ITEMS } from "../data/order";
import { STR, CAT_HI, ITEM_HI } from "../data/i18n";
import { SITE } from "../data/menu";
import { Flourish, PetalField, Blossom } from "../components/decor";

const API = "/api";
const GST_RATE = 0.05;

const VegBadge = ({ veg = true }) => (
  <span
    data-testid="veg-badge"
    title={veg ? "Pure Veg" : "Non-Veg"}
    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border-2 bg-white ${veg ? "border-green-700" : "border-[#8a4b2b]"}`}
  >
    {veg ? (
      <span className="h-1.5 w-1.5 rounded-full bg-green-700" />
    ) : (
      <span className="h-0 w-0 border-x-[4px] border-b-[7px] border-x-transparent border-b-[#8a4b2b]" />
    )}
  </span>
);

const DetailsForm = ({ details, setDetails, compact = false }) => (
  <div className={compact ? "flex flex-col gap-3" : "mt-6 grid gap-4 sm:grid-cols-2"}>
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">
        {details.type === "dinein" ? "Guest Name" : "Your Name"}
      </label>
      <input
        data-testid="order-name"
        value={details.name}
        onChange={(e) => setDetails({ ...details, name: e.target.value })}
        placeholder="Full name"
        className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white/90 px-4 py-3 text-sm text-[#2C2C2C] outline-none transition-colors focus:border-[#9B2C2C]"
      />
    </div>
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Phone</label>
      <input
        data-testid="order-phone"
        value={details.phone}
        onChange={(e) => setDetails({ ...details, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
        placeholder="10-digit mobile number"
        inputMode="numeric"
        className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white/90 px-4 py-3 text-sm text-[#2C2C2C] outline-none transition-colors focus:border-[#9B2C2C]"
      />
    </div>
    {details.type === "delivery" && (
      <div className={compact ? "" : "sm:col-span-2"}>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Delivery Address</label>
        <textarea
          data-testid="order-address"
          value={details.address}
          onChange={(e) => setDetails({ ...details, address: e.target.value })}
          placeholder="House / street / landmark, Dumka"
          rows={2}
          className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white/90 px-4 py-3 text-sm text-[#2C2C2C] outline-none transition-colors focus:border-[#9B2C2C]"
        />
      </div>
    )}
  </div>
);

const AddControl = ({ item, qty, soldOut, onAdd, onQty }) => {
  if (soldOut)
    return (
      <span data-testid={`sold-out-${item.id}`} className="rounded-lg bg-[#2C2C2C]/75 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
        Sold Out
      </span>
    );
  if (!qty)
    return (
      <button
        data-testid={`add-${item.id}`}
        onClick={onAdd}
        className="rounded-lg border border-[#9B2C2C] bg-white px-6 py-1.5 text-sm font-extrabold uppercase tracking-wide text-[#9B2C2C] shadow-[0_4px_14px_rgba(155,44,44,0.2)] transition-colors hover:bg-[#9B2C2C] hover:text-white"
      >
        ADD
      </button>
    );
  return (
    <div data-testid={`qty-${item.id}`} className="flex items-center gap-2.5 rounded-lg border border-[#9B2C2C] bg-white px-2 py-1 text-[#9B2C2C] shadow-[0_4px_14px_rgba(155,44,44,0.2)]">
      <button data-testid={`qty-minus-${item.id}`} onClick={() => onQty(qty - 1)} aria-label="Decrease quantity">
        <Minus size={14} strokeWidth={3} />
      </button>
      <span className="min-w-4 text-center text-sm font-extrabold">{qty}</span>
      <button data-testid={`qty-plus-${item.id}`} onClick={() => onQty(qty + 1)} aria-label="Increase quantity">
        <Plus size={14} strokeWidth={3} />
      </button>
    </div>
  );
};

const DishRow = ({ item, ordering, isSoldOut, cart, setQty, lang, onZoom }) => {
  const [expanded, setExpanded] = useState(false);
  const [picker, setPicker] = useState(false);
  const [sizeIdx, setSizeIdx] = useState(item.sizes?.length ? Math.min(1, item.sizes.length - 1) : 0);
  const [pickedAddons, setPickedAddons] = useState([]);
  const t = STR[lang] || STR.en;
  const soldOut = isSoldOut(item.id);
  const sizes = item.sizes || [];
  const addons = item.addons || [];
  const hasSizes = sizes.length > 0;
  const lineKey = hasSizes
    ? `${item.id}|${sizes[sizeIdx].label}${pickedAddons.length ? "|" + pickedAddons.join("+") : ""}`
    : item.id;
  const curQty = cart[lineKey] || 0;
  const qtyInCart = Object.entries(cart)
    .filter(([k, q]) => q > 0 && (k === item.id || k.startsWith(item.id + "|")))
    .reduce((s, [, q]) => s + q, 0);
  const currentUnit = hasSizes
    ? sizes[sizeIdx].price + pickedAddons.reduce((s, a) => s + (addons.find((x) => x.label === a)?.price || 0), 0)
    : item.price;
  const priceLabel = hasSizes ? `₹${sizes[0].price} – ₹${sizes[sizes.length - 1].price}` : `₹${item.price}`;
  const name = lang === "hi" ? ITEM_HI[item.id] || item.name : item.name;
  const altName = lang === "hi" ? item.name : ITEM_HI[item.id];

  const toggleAddon = (label) =>
    setPickedAddons((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));

  return (
    <div data-testid={`order-item-${item.id}`} className="py-5">
      <div className="flex gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <VegBadge veg={item.veg !== false} />
            <h3 className="font-serif text-base font-bold text-[#2C2C2C] md:text-lg">{name}</h3>
          </div>
          {altName && <p className="font-hindi mt-0.5 text-xs text-[#B86B77]">{altName}</p>}
          <p className="mt-1 font-serif text-sm font-semibold text-[#9B2C2C]">{priceLabel}</p>
          {item.desc && (
            <>
              <p className={`mt-1 text-sm text-[#8d7b7e] ${expanded ? "" : "line-clamp-1"}`}>{item.desc}</p>
              {item.desc.length > 60 && (
                <button
                  data-testid={`desc-toggle-${item.id}`}
                  onClick={() => setExpanded(!expanded)}
                  className="mt-0.5 text-xs font-semibold text-[#9B2C2C] underline-offset-2 hover:underline"
                >
                  {expanded ? t.less : t.more}
                </button>
              )}
            </>
          )}
        </div>
        <div className="relative w-[104px] shrink-0 self-start md:w-[124px]">
          <button
            data-testid={`zoom-${item.id}`}
            onClick={() => onZoom(item)}
            className="block w-full cursor-zoom-in"
            aria-label={`View photo of ${item.name}`}
          >
            <img
              key={item.img}
              src={item.img}
              alt={item.name}
              loading="lazy"
              className={`aspect-square w-full rounded-[14px] object-cover ${soldOut ? "opacity-50 grayscale" : ""}`}
            />
          </button>
          {qtyInCart > 0 && (
            <span className="absolute -left-2 -top-2 rounded-full bg-[#C9A227] px-2 py-0.5 text-[10px] font-bold text-white shadow">
              {qtyInCart} {t.inCart}
            </span>
          )}
          <div className={soldOut ? "absolute inset-0 flex items-center justify-center" : "absolute -bottom-3 left-1/2 -translate-x-1/2"}>
            {ordering && !hasSizes && (
              <AddControl item={item} qty={curQty} soldOut={soldOut} onAdd={() => setQty(lineKey, 1)} onQty={(q) => setQty(lineKey, q)} />
            )}
            {ordering && hasSizes && !soldOut && !picker && (
              <button
                data-testid={`add-${item.id}`}
                onClick={() => setPicker(!picker)}
                className="rounded-lg border border-[#9B2C2C] bg-white px-5 py-1.5 text-sm font-extrabold uppercase tracking-wide text-[#9B2C2C] shadow-[0_4px_14px_rgba(155,44,44,0.2)] transition-colors hover:bg-[#9B2C2C] hover:text-white"
              >
                {t.add}
              </button>
            )}
            {ordering && soldOut && (
              <span data-testid={`sold-out-${item.id}`} className="rounded-lg bg-[#2C2C2C]/75 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
                {t.soldOut}
              </span>
            )}
          </div>
        </div>
      </div>

      {picker && hasSizes && !soldOut && ordering && (
        <div data-testid={`variant-picker-${item.id}`} className="mt-3 rounded-[12px] border border-[#E8B4B8]/50 bg-white/85 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">{t.chooseSize}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {sizes.map((s, i) => (
              <button
                key={s.label}
                data-testid={`size-${item.id}-${i}`}
                onClick={() => setSizeIdx(i)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  i === sizeIdx ? "border-[#9B2C2C] bg-[#9B2C2C] text-white" : "border-[#E8B4B8]/60 bg-white text-[#9B2C2C] hover:border-[#9B2C2C]"
                }`}
              >
                {s.label} • ₹{s.price}
              </button>
            ))}
          </div>
          {addons.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {addons.map((a) => (
                <button
                  key={a.label}
                  data-testid={`addon-${item.id}-${a.label.replace(/\s/g, "-")}`}
                  onClick={() => toggleAddon(a.label)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                    pickedAddons.includes(a.label)
                      ? "border-[#C9A227] bg-[#C9A227] text-white"
                      : "border-[#E8B4B8]/60 bg-white text-[#B86B77] hover:border-[#C9A227]"
                  }`}
                >
                  {pickedAddons.includes(a.label) ? "✓ " : "+ "}
                  {a.label} ₹{a.price}
                </button>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between">
            <span className="font-serif text-lg font-bold text-[#9B2C2C]">₹{currentUnit}</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setPicker(false)} className="text-xs font-semibold text-[#B86B77] hover:text-[#9B2C2C]" aria-label="Close size picker">✕</button>
              <AddControl item={item} qty={curQty} soldOut={false} onAdd={() => setQty(lineKey, 1)} onQty={(q) => setQty(lineKey, q)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Order({ embedded = false }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bloomora-cart") || "{}"); } catch { return {}; }
  });
  const [details, setDetails] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem("bloomora-order-details") || "{}"); } catch { saved = {}; }
    return { type: "dinein", tableNo: "", name: "", phone: "", address: "", ...saved };
  });
  const [stock, setStock] = useState({});
  const [overrides, setOverrides] = useState({});
  const [extra, setExtra] = useState({ categories: [], items: [] });
  const [layout, setLayout] = useState({ cat_order: [], item_order: {}, hidden_cats: [] });
  const [lang, setLang] = useState(() => localStorage.getItem("bloomora-lang") || "en");
  const [zoomImg, setZoomImg] = useState(null);
  const [activeCat, setActiveCat] = useState("best");
  const [query, setQuery] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [error, setError] = useState("");
  const [utr, setUtr] = useState("");
  const [copied, setCopied] = useState(false);
  const stripRef = useRef(null);
  const ordering = !embedded && details.type === "delivery";

  useEffect(() => { localStorage.setItem("bloomora-lang", lang); }, [lang]);
  const t = STR[lang] || STR.en;

  useEffect(() => { localStorage.setItem("bloomora-cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("bloomora-order-details", JSON.stringify(details)); }, [details]);
  useEffect(() => { if (!ordering) setCartOpen(false); }, [ordering]);

  useEffect(() => {
    const load = () => {
      fetch(`${API}/menu-stock`).then((r) => r.json()).then(setStock).catch(() => {});
      fetch(`${API}/menu-overrides`).then((r) => r.json()).then(setOverrides).catch(() => {});
      fetch(`${API}/catalog-extra`).then((r) => r.json()).then(setExtra).catch(() => {});
      fetch(`${API}/catalog-layout`).then((r) => r.json()).then(setLayout).catch(() => {});
    };
    load();
    const t = setInterval(load, 3000);
    const onFocus = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", load);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", load);
    };
  }, []);

  useEffect(() => {
    if (query.trim()) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveCat(e.target.id.replace("order-cat-", ""))),
      { rootMargin: "-25% 0px -65% 0px" }
    );
    document.querySelectorAll('[id^="order-cat-"]').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, stock, overrides, ordering, extra, layout]);

  const stockOf = (id) => {
    const s = stock[id];
    if (s && typeof s === "object") return { online: s.online !== false, dinein: s.dinein !== false };
    return { online: s !== false, dinein: s !== false };
  };
  const isSoldOut = (id) => (ordering ? !stockOf(id).online : !stockOf(id).dinein);

  const allItems = useMemo(() => {
    const applyOverrides = (i) => {
      const o = overrides[i.id] || {};
      return {
        ...i,
        name: o.name ?? i.name,
        desc: o.desc ?? i.desc,
        veg: o.veg ?? i.veg ?? true,
        best: o.best ?? i.best ?? false,
        sizes: o.sizes ?? i.sizes,
        addons: o.addons ?? i.addons,
        price: o.price ?? i.price,
        img: o.img ?? (i.img || "/images/logo.webp"),
        deleted: o.deleted === true,
        order_index: o.order_index ?? 999,
      };
    };
    return [...ORDER_ITEMS.map(applyOverrides), ...(extra.items || []).map(applyOverrides)];
  }, [overrides, extra]);
  const visibleItems = useMemo(() => allItems.filter((i) => !i.deleted), [allItems]);
  const hiddenCats = layout.hidden_cats || [];
  const catRank = (id) => {
    const idx = (layout.cat_order || []).indexOf(id);
    return idx === -1 ? 999 : idx;
  };
  const ALL_CATEGORIES = useMemo(
    () =>
      [...ORDER_CATEGORIES, ...(extra.categories || []).map((c) => ({ id: c.slug, name: c.name.toUpperCase() }))]
        .filter((c) => !hiddenCats.includes(c.id))
        .sort((a, b) => catRank(a.id) - catRank(b.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extra, layout]
  );
  const catItems = (catId) =>
    visibleItems
      .filter((i) => (catId === "best" ? i.best : i.cat === catId))
      .sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999));
  const displayItems = (catId) => catItems(catId).filter((i) => ordering || stockOf(i.id).dinein);
  const activeCats = ALL_CATEGORIES.filter((c) => displayItems(c.id).length > 0);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return visibleItems.filter((i) => i.name.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q));
  }, [query, visibleItems]);

  useEffect(() => {
    stripRef.current?.querySelector(`[data-ocat="${activeCat}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeCat]);

  const byId = useMemo(() => Object.fromEntries(allItems.map((i) => [i.id, i])), [allItems]);

  const lineInfo = (key) => {
    const [id, sizeLabel, addonsStr] = key.split("|");
    const item = byId[id];
    if (!item) return null;
    let unit = item.price;
    let label = item.name;
    if (sizeLabel) {
      const s = (item.sizes || []).find((x) => x.label === sizeLabel);
      if (s) { unit = s.price; label += ` (${sizeLabel})`; }
    }
    if (addonsStr) {
      addonsStr.split("+").forEach((a) => {
        const ad = (item.addons || []).find((x) => x.label === a);
        if (ad) { unit += ad.price; label += ` + ${a}`; }
      });
    }
    return { item, unit, label };
  };

  const entries = Object.entries(cart).filter(([k, q]) => q > 0 && lineInfo(k));
  const count = entries.reduce((s, [, q]) => s + q, 0);
  const subtotal = entries.reduce((s, [k, q]) => s + lineInfo(k).unit * q, 0);
  const gst = Math.round(subtotal * GST_RATE);
  const total = subtotal + gst;
  const upiLink = `upi://pay?pa=Vyapar.178198447338@hdfcbank&pn=The%20Sweet%20Spot%20Savouries&am=${total}&cu=INR&tn=Bloomora%20Food%20Order`;

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText("Vyapar.178198447338@hdfcbank");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const setQty = (id, q) => setCart((c) => { const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = q; return n; });

  const scrollToCat = (id) => {
    const el = document.getElementById(`order-cat-${id}`);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -170, duration: 0.9 });
    else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 170, behavior: "smooth" });
  };

  const validate = () => {
    if (!details.name.trim()) return "Please enter your name";
    if (details.phone.length !== 10) return "Please enter a valid 10-digit phone number";
    if (details.address.trim().length < 10) return "Please enter your full delivery address";
    if (!entries.length) return "Your cart is empty";
    if (!/^[a-zA-Z0-9]{8,14}$/.test(utr.trim())) return "Please enter the UTR / transaction reference number from your UPI payment";
    return "";
  };

  const checkout = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setPlacing(true);
    const payload = {
      type: "delivery",
      table_no: "",
      name: details.name,
      phone: details.phone,
      address: details.address,
      items: entries.map(([k, q]) => {
        const li = lineInfo(k);
        return { id: k, name: li.label, price: li.unit, qty: q };
      }),
      subtotal, gst, total,
      payment_method: "upi",
      utr: utr.trim(),
    };
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setPlaced({ id: data.order_id, total, count });
      setCart({});
      setUtr("");
      setCartOpen(false);
    } catch {
      const lines = payload.items.map((i) => `${i.qty} x ${i.name} - Rs.${i.price * i.qty}`).join("\n");
      const msg = `New Order - Bloomora\nType: ${details.type === "dinein" ? `Dine-in (Table ${details.tableNo})` : "Delivery"}\nName: ${details.name}\nPhone: ${details.phone}\n${details.address ? `Address: ${details.address}\n` : ""}\n${lines}\nSubtotal: Rs.${subtotal}\nGST 5%: Rs.${gst}\nTotal: Rs.${total}`;
      window.open(`https://wa.me/918709317269?text=${encodeURIComponent(msg)}`, "_blank");
      setPlaced({ id: "WhatsApp", total, count });
      setCart({});
      setCartOpen(false);
    } finally {
      setPlacing(false);
    }
  };

  const rowProps = { ordering, isSoldOut, cart, setQty, lang, onZoom: setZoomImg };

  return (
    <main data-testid="order-page" className={embedded ? "" : "pb-32"}>
      {!embedded && (
      <section className="relative overflow-hidden px-5 pb-8 pt-14 text-center md:pt-20">
        <PetalField count={7} />
        <div className="flex items-center justify-center gap-3">
          <Blossom className="h-6 w-6 text-[#E8B4B8]" />
          <h1 className="font-serif text-4xl font-black text-[#9B2C2C] md:text-5xl">{t.orderOnline}</h1>
          <Blossom className="h-6 w-6 text-[#E8B4B8]" />
        </div>
        <p className="mt-2 font-serif text-sm italic text-[#B86B77]">Eat Bloom Repeat — fresh from our kitchen to your table</p>
        <div className="mt-4 flex justify-center"><Flourish className="h-4 w-48" /></div>

        <div data-testid="order-type-toggle" className="mt-7 inline-flex rounded-full border border-[#E8B4B8]/50 bg-white/70 p-1.5 shadow-[0_6px_20px_rgba(155,44,44,0.08)] backdrop-blur-sm">
          {[
            { id: "dinein", label: t.dinein, icon: UtensilsCrossed },
            { id: "delivery", label: t.delivery, icon: Bike },
          ].map((tg) => (
            <button
              key={tg.id}
              data-testid={`order-type-${tg.id}`}
              onClick={() => setDetails({ ...details, type: tg.id })}
              className={`relative flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold transition-colors duration-300 md:px-6 md:text-sm ${
                details.type === tg.id ? "text-white" : "text-[#9B2C2C] hover:text-[#7A1D28]"
              }`}
            >
              {details.type === tg.id && (
                <motion.span layoutId="order-type-pill" className="absolute inset-0 rounded-full bg-[#9B2C2C]" transition={{ type: "spring", stiffness: 320, damping: 30 }} />
              )}
              <span className="relative z-10 flex items-center gap-2"><tg.icon size={15} /> {tg.label}</span>
            </button>
          ))}
        </div>

        {details.type === "delivery" ? (
          <div className="mx-auto max-w-2xl">
            <DetailsForm details={details} setDetails={setDetails} />
          </div>
        ) : (
          <p data-testid="dinein-browse-note" className="mt-5 font-serif text-sm italic text-[#B86B77]">
            {t.browseNote}
          </p>
        )}
      </section>
      )}

      <div data-testid="order-category-strip" className="sticky top-[65px] z-40 border-y border-[#E8B4B8]/40 bg-[#F8E8E8]/92 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 pt-3">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B86B77]" />
            <input
              data-testid="order-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPh}
              className="w-full rounded-full border border-[#E8B4B8]/60 bg-white/95 py-3 pl-11 pr-10 text-sm text-[#2C2C2C] shadow-[0_4px_16px_rgba(155,44,44,0.07)] outline-none transition-colors placeholder:text-[#B86B77]/80 focus:border-[#9B2C2C]"
            />
            {query && (
              <button
                data-testid="order-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#9B2C2C] hover:bg-[#E8B4B8]/30"
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="shrink-0 rounded-full border-2 border-[#C9A227] bg-white/90 px-3.5 py-2.5 text-xs font-bold text-[#9B2C2C] shadow-[0_4px_16px_rgba(155,44,44,0.07)] transition-colors hover:bg-[#C9A227] hover:text-white"
            aria-label="Switch language"
          >
            {lang === "en" ? "हिंदी" : "EN"}
          </button>
        </div>
        <div ref={stripRef} className="no-scrollbar mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 py-3">
          {activeCats.map((c) => (
            <button
              key={c.id}
              data-ocat={c.id}
              data-testid={`order-tab-${c.id}`}
              onClick={() => scrollToCat(c.id)}
              className={`relative whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 md:text-sm ${
                activeCat === c.id ? "text-[#9B2C2C]" : "text-[#B86B77] hover:text-[#9B2C2C]"
              }`}
            >
              {c.name && (lang === "hi" ? CAT_HI[c.id] || c.name : c.name)}
              {activeCat === c.id && (
                <motion.span layoutId="order-cat-underline" className="absolute inset-x-3 -bottom-0.5 h-[2.5px] rounded-full bg-[#9B2C2C]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 md:px-6">
        {query.trim() ? (
          <section data-testid="order-search-results" className="pt-6">
            <h2 className="font-serif text-xl font-bold text-[#9B2C2C]">
              {searchResults.filter((i) => ordering || stockOf(i.id).dinein).length
                ? t.resultsFor(query.trim())
                : t.noResults(query.trim())}
            </h2>
            <div className="mt-2 h-[2px] w-16 bg-[#C9A227]" />
            <div className="mt-2 divide-y divide-[#E8B4B8]/50 pb-6">
              {searchResults.filter((i) => ordering || stockOf(i.id).dinein).map((item) => (
                <DishRow key={`search-${item.id}`} item={item} {...rowProps} />
              ))}
            </div>
          </section>
        ) : (
          activeCats.map((cat) => (
            <section key={cat.id} id={`order-cat-${cat.id}`} data-testid={`order-section-${cat.id}`} className="pt-8">
              <h2 className="font-serif text-xl font-bold text-[#9B2C2C] md:text-2xl">{lang === "hi" ? CAT_HI[cat.id] || cat.name : cat.name}</h2>
              <div className="mt-2 h-[2px] w-16 bg-[#C9A227]" />
              <div className="mt-2 divide-y divide-[#E8B4B8]/50">
                {displayItems(cat.id).map((item) => (
                  <DishRow key={`${cat.id}-${item.id}`} item={item} {...rowProps} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      <AnimatePresence>
        {ordering && count > 0 && !cartOpen && (
          <motion.div
            data-testid="cart-bar"
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
          >
            <div className="flex w-full max-w-xl items-center justify-between gap-3 rounded-full border border-[#C9A227]/40 bg-[#9B2C2C] py-3 pl-6 pr-2 text-white shadow-[0_16px_44px_rgba(122,29,40,0.45)]">
              <div className="flex items-center gap-3">
                <ShoppingBag size={19} />
                <div className="leading-tight">
                  <p data-testid="cart-bar-count" className="text-sm font-semibold">{count} {t.items}</p>
                  <p data-testid="cart-bar-subtotal" className="text-xs text-white/75">₹{subtotal} + GST</p>
                </div>
              </div>
              <button
                data-testid="view-cart-button"
                onClick={() => setCartOpen(true)}
                className="rounded-full bg-white px-6 py-2.5 text-sm font-bold text-[#9B2C2C] transition-transform hover:scale-105"
              >
                {t.viewCart}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cartOpen && (
          <motion.div data-testid="cart-overlay" className="fixed inset-0 z-[70] bg-[#2C2C2C]/40 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setCartOpen(false)}>
            <motion.aside
              data-testid="cart-drawer"
              onClick={(e) => e.stopPropagation()}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-[#FFF7F8]"
            >
              <div className="flex items-center justify-between border-b border-[#E8B4B8]/40 px-6 py-4">
                <h2 className="font-serif text-2xl font-bold text-[#9B2C2C]">{t.yourCart}</h2>
                <button data-testid="cart-close" onClick={() => setCartOpen(false)} className="rounded-full p-2 text-[#9B2C2C] hover:bg-[#E8B4B8]/30" aria-label="Close cart">
                  <X size={22} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4" data-lenis-prevent>
                {entries.map(([k, q]) => {
                  const li = lineInfo(k);
                  return (
                    <div key={k} data-testid={`cart-item-${k}`} className="flex items-center justify-between gap-3 border-b border-[#E8B4B8]/25 py-3.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#2C2C2C]">{li.label}</p>
                        <p className="text-xs text-[#B86B77]">₹{li.unit} each</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 rounded-full border border-[#9B2C2C]/30 p-0.5 text-[#9B2C2C]">
                          <button onClick={() => setQty(k, q - 1)} className="rounded-full p-1 hover:bg-[#E8B4B8]/30" aria-label="Decrease"><Minus size={13} /></button>
                          <span className="w-5 text-center text-sm font-bold">{q}</span>
                          <button onClick={() => setQty(k, q + 1)} className="rounded-full p-1 hover:bg-[#E8B4B8]/30" aria-label="Increase"><Plus size={13} /></button>
                        </div>
                        <span className="w-14 text-right font-serif text-sm font-semibold text-[#9B2C2C]">₹{li.unit * q}</span>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-5 rounded-[12px] bg-white/80 p-4 text-sm">
                  <div className="flex justify-between py-1 text-[#2C2C2C]"><span>{t.subtotal}</span><span data-testid="cart-subtotal">₹{subtotal}</span></div>
                  <div className="flex justify-between py-1 text-[#B86B77]"><span>{t.gst}</span><span data-testid="cart-gst">₹{gst}</span></div>
                  <div className="mt-2 flex justify-between border-t border-[#E8B4B8]/40 pt-2 font-serif text-lg font-bold text-[#9B2C2C]">
                    <span>{t.total}</span><span data-testid="cart-total">₹{total}</span>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="mb-3 font-serif text-lg font-semibold text-[#9B2C2C]">{t.deliveryDetails}</p>
                  <DetailsForm details={details} setDetails={setDetails} compact />
                </div>

                <div data-testid="upi-payment-panel" className="mt-5 rounded-[12px] border border-[#C9A227]/40 bg-white/85 p-5">
                  <p className="font-serif text-lg font-semibold text-[#9B2C2C]">{t.payUpi}</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#B86B77]">
                    {t.upiNote}
                  </p>
                  <div className="mt-4 flex flex-col items-center">
                    <img
                      src="/images/upi-qr.jpg"
                      alt="Bloomora UPI QR — The Sweet Spot Savouries, HDFC SmartHub Vyapar"
                      data-testid="upi-qr-image"
                      className="w-44 rounded-[12px] border border-[#E8B4B8]/50 shadow-sm"
                    />
                    <p className="mt-3 text-sm font-bold tracking-wide text-[#2C2C2C]">THE SWEET SPOT SAVOURIES</p>
                    <button
                      onClick={copyUpiId}
                      data-testid="upi-id-copy"
                      className="mt-1.5 flex items-center gap-2 rounded-full bg-[#F8E8E8] px-4 py-2 text-xs font-semibold text-[#9B2C2C] transition-colors hover:bg-[#E8B4B8]/40"
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      Vyapar.178198447338@hdfcbank
                    </button>
                    <a
                      href={upiLink}
                      data-testid="upi-pay-button"
                      className="group relative mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#9B2C2C] py-4 text-center text-sm font-bold text-white shadow-[0_14px_36px_rgba(122,29,40,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#7A1D28]"
                    >
                      Pay ₹{total} via GPay / PhonePe / Paytm
                      <span className="absolute bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/3" />
                    </a>
                    <p className="mt-2 text-center text-[11px] leading-relaxed text-[#B86B77]">
                      {t.upiOpens(total)}
                    </p>
                    <input
                      data-testid="utr-input"
                      value={utr}
                      onChange={(e) => { setUtr(e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 14)); setError(""); }}
                      placeholder={t.utrPh}
                      inputMode="numeric"
                      className="mt-4 w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-3 text-center text-sm font-semibold tracking-wider text-[#2C2C2C] outline-none transition-colors focus:border-[#9B2C2C]"
                    />
                    <p className="mt-1.5 text-center text-[11px] text-[#B86B77]">
                      {t.utrHelp}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E8B4B8]/40 px-6 py-4">
                {error && <p data-testid="checkout-error" className="mb-3 rounded-lg bg-[#9B2C2C]/10 px-4 py-2 text-center text-xs font-semibold text-[#9B2C2C]">{error}</p>}
                <button
                  data-testid="checkout-button"
                  onClick={checkout}
                  disabled={placing}
                  className="group relative w-full rounded-full bg-[#9B2C2C] py-4 font-semibold text-white transition-colors hover:bg-[#7A1D28] disabled:opacity-60"
                >
                  {placing ? t.placing : t.checkout(total)}
                  <span className="absolute bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/3" />
                </button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {placed && (
          <motion.div data-testid="order-success" className="fixed inset-0 z-[80] flex items-center justify-center bg-[#F8E8E8]/95 p-5 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-sm rounded-[16px] border border-[#E8B4B8]/40 bg-white p-8 text-center shadow-2xl">
              <CheckCircle2 className="mx-auto h-14 w-14 text-green-700" />
              <h2 className="mt-4 font-serif text-2xl font-bold text-[#9B2C2C]">{t.orderPlaced}</h2>
              <p className="mt-2 text-sm text-[#2C2C2C]">
                {t.orderId} <span data-testid="order-success-id" className="font-bold text-[#9B2C2C]">{placed.id}</span>
              </p>
              <p className="mt-1 text-sm text-[#B86B77]">{placed.count} {t.items} • ₹{placed.total} — {t.teamConfirm}</p>
              <p className="mt-1 text-xs text-green-800">{t.payVerify}</p>
              <a
                href={SITE.phoneHref}
                data-testid="order-success-call"
                className="mt-6 block rounded-full border-2 border-[#C9A227] py-3 text-sm font-semibold text-[#9B2C2C] transition-colors hover:bg-[#C9A227] hover:text-white"
              >
                Call {SITE.phone} for updates
              </a>
              <button
                data-testid="order-success-close"
                onClick={() => setPlaced(null)}
                className="mt-3 w-full rounded-full bg-[#9B2C2C] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#7A1D28]"
              >
                {t.placeAnother}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {zoomImg && (
          <motion.div
            data-testid="dish-zoom-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#2C2C2C]/70 p-5 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoomImg(null)}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomImg.img}
                alt={zoomImg.name}
                className="max-h-[70vh] w-full rounded-[16px] border-4 border-white object-contain bg-white shadow-2xl"
              />
              <figcaption className="mt-3 text-center font-serif text-lg font-semibold text-white">
                {zoomImg.name} <span className="text-[#E8B4B8]">• ₹{zoomImg.price}</span>
              </figcaption>
              <button
                data-testid="dish-zoom-close"
                onClick={() => setZoomImg(null)}
                className="absolute -right-2 -top-2 rounded-full bg-[#9B2C2C] p-2.5 text-white shadow-lg transition-transform hover:scale-110"
                aria-label="Close image preview"
              >
                <X size={18} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
